import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import StatementStatusCode from 'models/enums/StatementStatusCode';
import { useStatementProvider } from '../Statement.Hooks';

enum EmailStatusCode {
  IS_SENT = 'SENT',
  IS_NOT_SENT = 'NOT_SENT',
}

export default function StatementFilters() {
  const { lang, translate } = getLocales();
  const { listApi, dealersApi, dealerGroupsApi, userListApi } = useStatementProvider();
  const { isDealer } = useAuthStore();

  return (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      filters={listApi.body}
      onFilter={(newFilters) => listApi.execute(newFilters)}
      search={listApi.body?.search}
      onSearch={(search) => listApi.execute((body) => ({ ...body, search }))}
      onSortById={(sort) => listApi.execute((body) => ({ ...body, ...sort }))}
      onReset={listApi.reset}
      onOpen={() => {
        if (!dealersApi.payload) dealersApi.execute((body) => body);
        if (!dealerGroupsApi.payload) dealerGroupsApi.execute((body) => body);
        if (!userListApi.payload) userListApi.execute((body) => body);
      }}
      render={({ tempFilters, setFilters }) => {
        const filterUsers = (dealerIds?: number[], distributorIds?: number[]) =>
          userListApi?.payload?.data?.filter((user) => {
            const matchesDistributor =
              !distributorIds?.length || distributorIds.some((id) => id === user.distributorId);
            const matchesDealer =
              !dealerIds?.length || user.dealers?.some((dealer) => dealerIds.some((id) => id === dealer.id));
            const hasUpdateStatementStatusPermission =
              user.roles?.some((role) =>
                role.permissions?.some((permission) => permission.key === 'UPDATE_STATEMENT_STATUS')
              ) ||
              user.additionalPermissions?.some(
                (additionalPermission) => additionalPermission.permission?.key === 'UPDATE_STATEMENT_STATUS'
              );

            return matchesDistributor && matchesDealer && hasUpdateStatementStatusPermission;
          }) ?? [];

        const filteredUsers = filterUsers(tempFilters?.dealerId, tempFilters?.distributorId);

        return (
          <>
            <SelectInput
              name="status"
              label={lang.status}
              data={Object.values(StatementStatusCode).map((it) => ({
                id: it,
                title: translate(it),
                icon: <Icon class="fas-check" />,
              }))}
              isMultiselect
              value={tempFilters?.status}
              onChange={({ status }) => setFilters({ ...tempFilters, status })}
              icon={<Icon class="fas-cloud" />}
            />
            <SelectInput
              name="isEmailSent"
              label={lang.email}
              data={[
                { id: EmailStatusCode.IS_SENT, title: lang.sent, icon: <Icon class="fas-check" /> },
                { id: EmailStatusCode.IS_NOT_SENT, title: lang.notSent, icon: <Icon class="fas-times" /> },
              ]}
              value={
                tempFilters?.isEmailSent === true
                  ? EmailStatusCode.IS_SENT
                  : tempFilters?.isEmailSent === false
                    ? EmailStatusCode.IS_NOT_SENT
                    : null
              }
              onChange={({ isEmailSent }) => {
                let parsedValue: boolean | null;
                if (isEmailSent === EmailStatusCode.IS_SENT) parsedValue = true;
                else if (isEmailSent === EmailStatusCode.IS_NOT_SENT) parsedValue = false;
                else parsedValue = null;

                setFilters({ isEmailSent: parsedValue });
              }}
              icon={<Icon class="fas-cloud" />}
            />
            {dealersApi?.payload?.data?.length > 1 && (
              <SelectInput
                name="dealerId"
                label={lang.dealers}
                data={dealersApi?.payload?.data?.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                value={tempFilters?.dealerId}
                onChange={({ dealerId }) =>
                  setFilters({
                    dealerId,
                    reconciledById: tempFilters?.reconciledById?.filter((id) =>
                      filterUsers(dealerId, tempFilters?.distributorId).some((user) => user.id === id)
                    ),
                  })
                }
                disabled={isDealer}
                icon={<Icon class="fas-cloud" />}
              />
            )}
            <SelectInput
              name="reconciledById"
              label={lang.reconciledBy}
              data={filteredUsers.map((user) => ({
                id: user.id,
                title: user.fullName,
                icon: <Icon class="fas-user" />,
              }))}
              isMultiselect
              value={tempFilters?.reconciledById}
              onChange={setFilters}
              disabled={!filteredUsers.length}
              icon={<Icon class="fas-user" />}
            />
            {dealerGroupsApi?.payload?.data?.length > 1 && (
              <SelectInput
                name="dealerGroupId"
                label={lang.dealerGroup}
                data={dealerGroupsApi?.payload?.data?.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                value={tempFilters?.dealerGroupId}
                onChange={setFilters}
                icon={<Icon class="fas-cloud" />}
              />
            )}
            <DateInput
              name="invoicingFromDateUtc"
              label={lang.invoicingFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.invoicingFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="invoicingToDateUtc"
              label={lang.invoicingToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.invoicingToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="modifiedAtFromDateUtc"
              label={lang.modifiedAtFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.modifiedAtFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="modifiedAtToDateUtc"
              label={lang.modifiedAtToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.modifiedAtToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
          </>
        );
      }}
    />
  );
}
